import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API_URL } from '@/config/api';

export default function AuthMagic() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying | success | error

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) { setStatus('error'); return; }
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/auth/magic-link/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token })
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || 'Invalid link');
        login(d.access_token, d.user);
        setStatus('success');
        setTimeout(() => navigate('/', { replace: true }), 1200);
      } catch {
        setStatus('error');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-50 p-5" data-testid="auth-magic">
      <div className="bg-white rounded-3xl shadow-xl p-8 w-full max-w-md text-center">
        {status === 'verifying' && (
          <>
            <Loader2 className="h-12 w-12 text-primary animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-secondary-900">Signing you in…</h2>
            <p className="text-muted-foreground text-sm mt-2">Verifying your login link</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-secondary-900">Welcome back!</h2>
            <p className="text-muted-foreground text-sm mt-2">Taking you to your home…</p>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-secondary-900">Link expired</h2>
            <p className="text-muted-foreground text-sm mt-2 mb-5">This login link is invalid or has already been used.</p>
            <button onClick={() => navigate('/auth')} className="h-12 px-6 bg-primary text-white rounded-2xl font-semibold" data-testid="magic-back-btn">
              Back to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}
